import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

// Context and Utils
import { useTheme } from '../context/ThemeContext';
import { useMissions } from '../context/MissionsContext';
import { useNotifications } from '../context/NotificationsContext';
import { getThemeColors } from '../utils/themeStyles';
import ErrorRetry from '../components/ErrorRetry';

type NotifType = 'chapter' | 'chat' | 'quiz' | 'card' | 'badge' | 'leaderboard' | 'mission' | 'system';

interface Notification {
  id: number;
  type: NotifType;
  title: string;
  message: string;
  time: string;
  unread: boolean;
  icon: string;
  color: string;
}

const TYPE_ICON: Record<string, string> = {
  chapter: 'book-open', chat: 'chatbox', quiz: 'brain', card: 'card',
  mission: 'trophy', badge: 'trophy', leaderboard: 'trending-up', system: 'notifications',
};

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `il y a ${d} j`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  
  const { cartItems } = useMissions();
  const { items, loading, error, refresh, markRead, markAllRead } = useNotifications();
  const [filter, setFilter] = useState('all');

  const getNotificationColor = (type: string) => {
    return colors.notificationColors[type as keyof typeof colors.notificationColors] || colors.textSecondary;
  };

  // API -> forme d'affichage (icône/couleur/temps relatif, dépendants du thème).
  const notifications: Notification[] = items.map(n => {
    const type = (TYPE_ICON[n.type] ? n.type : 'system') as NotifType;
    return {
      id: n.id,
      type,
      title: n.title,
      message: n.message,
      time: relativeTime(n.createdAt),
      unread: n.unread,
      icon: TYPE_ICON[type] || 'notifications',
      color: getNotificationColor(type),
    };
  });

  const filteredNotifications = filter === 'all'
    ? notifications
    : notifications.filter(n => n.type === filter);

  const unreadCount = notifications.filter(n => n.unread).length;

  const markAllAsRead = () => { void markAllRead(); };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.unread) void markRead(notification.id);
  };

  const getFilterCounts = () => {
    const counts = {
      all: notifications.length,
      chat: notifications.filter(n => n.type === 'chat').length,
      chapter: notifications.filter(n => n.type === 'chapter').length,
      quiz: notifications.filter(n => n.type === 'quiz').length,
      card: notifications.filter(n => n.type === 'card').length,
      mission: notifications.filter(n => n.type === 'mission').length,
      badge: notifications.filter(n => n.type === 'badge').length,
      leaderboard: notifications.filter(n => n.type === 'leaderboard').length,
      system: notifications.filter(n => n.type === 'system').length
    };
    return counts;
  };

  const counts = getFilterCounts();

  const renderFilterTab = (filterType: string, label: string, count: number) => (
    <TouchableOpacity
      key={filterType}
      style={[
        styles.filterTab,
        { backgroundColor: filter === filterType ? colors.primary : colors.surfaceSecondary }
      ]}
      onPress={() => setFilter(filterType)}
    >
      <Text style={[
        styles.filterTabText,
        { color: filter === filterType ? colors.badgeText : colors.textSecondary }
      ]}>
        {label} ({count})
      </Text>
    </TouchableOpacity>
  );

  const renderNotification = (notification: Notification) => {
    const getIconName = (icon: string) => {
      switch (icon) {
        case 'book-open': return 'book';
        case 'chatbox': return 'chatbox';
        case 'brain': return 'bulb';
        case 'card': return 'card';
        case 'trophy': return 'trophy';
        case 'trending-up': return 'trending-up';
        case 'notifications': return 'notifications';
        default: return 'notifications';
      }
    };

    return (
      <TouchableOpacity 
        key={notification.id} 
        style={[
          styles.notificationCard,
          { 
            backgroundColor: notification.unread ? colors.unreadBackground : colors.cardBackground,
            shadowColor: colors.cardShadow,
            borderLeftColor: notification.unread ? colors.unreadBorder : 'transparent',
            borderLeftWidth: notification.unread ? 4 : 0,
          }
        ]}
        onPress={() => handleNotificationClick(notification)}
      >
        <View style={styles.notificationContent}>
                          <View style={styles.notificationIconContainer}>
                  <View style={[styles.iconBackground, { backgroundColor: `${notification.color}20` }]}>
              <Ionicons 
                name={getIconName(notification.icon) as any} 
                size={16} 
                color={notification.color} 
              />
            </View>
          </View>
          
          <View style={styles.notificationText}>
            <View style={styles.notificationHeader}>
              <Text style={[
                styles.notificationTitle,
                { color: notification.unread ? colors.textPrimary : colors.textSecondary },
                notification.unread && styles.unreadTitle
              ]}>
                {notification.title}
              </Text>
              {notification.unread && (
                <View style={[styles.unreadDot, { backgroundColor: notification.color }]} />
              )}
            </View>
            
            <Text style={[
              styles.notificationMessage,
              { color: notification.unread ? colors.textPrimary : colors.textSecondary }
            ]}>
              {notification.message}
            </Text>
            
            <View style={styles.notificationFooter}>
              <Text style={[styles.timestamp, { color: colors.textTertiary }]}>
                {notification.time}
              </Text>
              <View style={[styles.typeBadge, { borderColor: colors.borderSecondary }]}>
                <Text style={[styles.typeBadgeText, { color: colors.textSecondary }]}>
                  {notification.type}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <View style={styles.iconContainer}>
              <Ionicons name="notifications" size={24} color={colors.iconPrimary} />
              {unreadCount > 0 && (
                <View style={[styles.badge, { backgroundColor: colors.badgeBackground }]}>
                  <Text style={[styles.badgeText, { color: colors.badgeText }]}>{unreadCount}</Text>
                </View>
              )}
            </View>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Notifications</Text>
          </View>
          <TouchableOpacity 
            style={styles.cartButton}
            onPress={() => navigation.navigate('Cart' as never)}
          >
            <Ionicons name="cart" size={20} color={colors.iconPrimary} />
            {cartItems.length > 0 && (
              <View style={[styles.cartBadge, { backgroundColor: colors.badgeBackground }]}>
                <Text style={[styles.cartBadgeText, { color: colors.badgeText }]}>{cartItems.length}</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
        
        {unreadCount > 0 && (
          <View style={styles.unreadSection}>
            <Text style={[styles.unreadText, { color: colors.textSecondary }]}>
              You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Text>
            <TouchableOpacity 
              style={[styles.markAllButton, { borderColor: colors.borderSecondary }]} 
              onPress={markAllAsRead}
            >
              <Text style={[styles.markAllButtonText, { color: colors.textSecondary }]}>
                Mark all read
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

             {/* Filter Tabs */}
       <View style={[styles.filtersContainer, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
         <View style={styles.filterHeader}>
           <Ionicons name="filter" size={16} color={colors.iconSecondary} />
           <Text style={[styles.filterTitle, { color: colors.textPrimary }]}>Filter by Type</Text>
         </View>
         <View style={styles.filterSection}>
           <View style={styles.filterOptions}>
             {renderFilterTab('all', 'All', counts.all)}
             {renderFilterTab('chat', 'Chat', counts.chat)}
             {renderFilterTab('chapter', 'Chapters', counts.chapter)}
             {renderFilterTab('quiz', 'Quizzes', counts.quiz)}
             {renderFilterTab('card', 'Cards', counts.card)}
           </View>
         </View>
       </View>

      {/* Notifications List */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.notificationsList}>
          {error && items.length === 0 ? (
            <ErrorRetry onRetry={() => void refresh()} retrying={loading} message="Impossible de charger les notifications." />
          ) : loading && items.length === 0 ? (
            <View style={styles.emptyState}>
              <ActivityIndicator color={colors.primary} />
            </View>
          ) : filteredNotifications.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="notifications" size={48} color={colors.textTertiary} />
              <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>Aucune notification</Text>
              <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>Vous êtes à jour !</Text>
            </View>
          ) : (
            filteredNotifications.map(renderNotification)
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    borderBottomWidth: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    position: 'relative',
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -6,
    borderRadius: 12,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  cartButton: {
    position: 'relative',
    padding: 8,
  },
  cartBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  unreadSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  unreadText: {
    fontSize: 14,
  },
  markAllButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 6,
  },
  markAllButtonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  filtersContainer: {
    padding: 16,
    borderBottomWidth: 1,
  },
  filterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  filterTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  filterSection: {
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterTab: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  activeFilterTab: {
    // Active state handled by dynamic styles
  },
  filterTabText: {
    fontSize: 11,
    fontWeight: '500',
  },
  activeFilterTabText: {
    // Active state handled by dynamic styles
  },
  content: {
    flex: 1,
  },
  notificationsList: {
    padding: 16,
  },
  notificationCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  notificationContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  notificationIconContainer: {
    marginRight: 12,
  },
  iconBackground: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationText: {
    flex: 1,
  },
  notificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  notificationTitle: {
    fontSize: 16,
    flex: 1,
    marginRight: 8,
  },
  unreadTitle: {
    fontWeight: '600',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  notificationMessage: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  notificationFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  timestamp: {
    fontSize: 12,
  },
  typeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderWidth: 1,
    borderRadius: 4,
  },
  typeBadgeText: {
    fontSize: 10,
    textTransform: 'capitalize',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
  },
}); 